<app-page-loader *ngIf="loading"></app-page-loader>

<ng-container *ngIf="!loading">
  <div *ngIf="error" class="cp-message">
    <p>{{ "components.competency.error" | translate }}</p>
    <button nbButton size="small" status="basic" type="button" (click)="retry.emit()">
      {{ "components.competency.retry" | translate }}
    </button>
  </div>

  <p *ngIf="!error && !view" class="cp-message">{{ "components.competency.empty" | translate }}</p>

  <ng-container *ngIf="!error && view">
    <div class="cp-toolbar">
      <nb-select
        *ngIf="courseOptions.length > 1"
        class="cp-course"
        size="small"
        [selected]="courseId"
        (selectedChange)="selectCourse($event)"
        [attr.aria-label]="'components.competency.course' | translate"
      >
        <nb-option *ngFor="let o of courseOptions; trackBy: trackOption" [value]="o.value">{{ o.label }}</nb-option>
      </nb-select>
      <span *ngIf="courseOptions.length === 1" class="cp-course-name">{{ courseOptions[0].label }}</span>

      <app-segmented-control
        *ngIf="courseId !== OVERALL"
        [options]="lensOptions"
        [value]="lens"
        [ariaLabel]="'components.competency.lensLabel' | translate"
        (valueChange)="selectLens($event)"
      ></app-segmented-control>

      <app-segmented-control
        *ngIf="view.chart !== 'list'"
        class="cp-mode"
        [options]="chartOptions"
        [value]="chartMode"
        [ariaLabel]="'components.competency.viewLabel' | translate"
        (valueChange)="selectChartMode($event)"
      ></app-segmented-control>
    </div>

    <p class="cp-lens-hint">
      {{ (courseId === OVERALL ? "components.competency.lensHints.courses" : "components.competency.lensHints." + lens) | translate }}
    </p>

    <div class="cp-stats">
      <span class="cp-stats__main">
        {{ "components.competency.summaryMeasured" | translate: { measured: view.summary.measured, total: view.summary.objectives } }}
      </span>
      <span *ngFor="let c of statusChips" class="al-status-chip" [ngClass]="'al-status-chip--' + c.tone">{{ c.text }}</span>
    </div>

    <p *ngIf="view.empty" class="cp-note">{{ "components.competency.emptyCourse" | translate }}</p>

    <div class="cp-chart" *ngIf="showChart">
      <div class="cp-chart__figure">
        <app-competency-radar
          *ngIf="view.chart === 'radar' && radarTexts"
          [axes]="radarAxes"
          [texts]="radarTexts"
          [showCoverage]="showCoverage"
          [ariaLabel]="view.aria"
          (axisSelect)="openArea($event)"
        ></app-competency-radar>

        <div *ngIf="view.chart === 'matrix'" class="cp-matrix">
          <div *ngFor="let row of matrix" class="cp-matrix__row">
            <h6 class="cp-matrix__group">{{ row.title }}</h6>
            <div class="cp-matrix__cells">
              <button
                *ngFor="let cell of row.cells; trackBy: trackAxis"
                type="button"
                class="cp-matrix__cell"
                [class.is-quiet]="cell.state === 'not_captured'"
                (click)="openArea(cell.key)"
              >
                <span class="cp-matrix__title">{{ cell.label }}</span>
                <span class="cp-matrix__hint">{{ cell.hint }}</span>
                <app-level-meter
                  *ngIf="cell.state !== 'not_captured'"
                  [level]="cell.level"
                  [verified]="cell.verified"
                  [label]="cell.levelLabel"
                ></app-level-meter>
                <span *ngIf="cell.state === 'not_captured'" class="cp-matrix__none">{{ cell.levelLabel }}</span>
                <span class="cp-matrix__measured">{{ cell.measuredText }}</span>
              </button>
            </div>
          </div>
          <p class="cp-matrix__kmk">{{ "components.competency.dqrKmk" | translate }}</p>
        </div>
      </div>

      <aside class="cp-reading" [attr.aria-label]="'components.competency.readingTitle' | translate">
        <h6>{{ "components.competency.readingTitle" | translate }}</h6>
        <ul>
          <li *ngIf="view.chart === 'radar'">
            <span class="cp-key cp-key--level" aria-hidden="true"></span>
            {{ "components.competency.reading.level" | translate }}
          </li>
          <li *ngIf="view.chart === 'radar' && showCoverage">
            <span class="cp-key cp-key--coverage" aria-hidden="true"></span>
            {{ "components.competency.reading.coverage" | translate }}
          </li>
          <li *ngIf="view.chart === 'radar'">{{ "components.competency.reading.rings" | translate }}</li>
          <li *ngIf="view.chart === 'matrix'">{{ "components.competency.reading.matrix" | translate }}</li>
          <li *ngIf="view.furthest.length">
            {{ "components.competency.reading.furthest" | translate: { names: view.furthest.join(", ") } }}
          </li>
          <li *ngIf="view.open.length">
            {{ "components.competency.reading.open" | translate: { names: view.open.join(", ") } }}
          </li>
        </ul>
        <nb-checkbox *ngIf="view.chart === 'radar'" [checked]="showCoverage" (checkedChange)="toggleCoverage($event)">
          {{ "components.competency.showCoverage" | translate }}
        </nb-checkbox>
      </aside>
    </div>

    <p *ngFor="let n of view.notes" class="cp-note">{{ n }}</p>

    <div class="cp-areas">
      <details
        *ngFor="let a of view.axes; trackBy: trackAxis"
        class="al-disclosure"
        [id]="areaId(a.key)"
        [open]="openKeys.has(a.key)"
        (toggle)="onToggle(a.key, $event)"
      >
        <summary>
          <span class="cp-area__label">
            <span class="cp-area__title">{{ a.label }}</span>
            <span *ngIf="a.hint" class="cp-area__hint">{{ a.hint }}</span>
          </span>
          <app-level-meter
            class="cp-area__meter"
            [level]="a.level"
            [verified]="a.verified"
            [label]="a.levelLabel"
            [muted]="a.state === 'not_captured' || a.state === 'open'"
          ></app-level-meter>
          <span class="cp-area__measured">{{ a.measuredText }}</span>
          <span class="cp-area__chips">
            <span *ngFor="let c of a.chips" class="al-status-chip" [ngClass]="'al-status-chip--' + c.tone">{{ c.text }}</span>
          </span>
        </summary>
        <div class="cp-area__body" *ngIf="openKeys.has(a.key)">
          <p *ngIf="a.note" class="cp-note">{{ a.note }}</p>
          <app-table
            *ngIf="a.rows.length"
            [title]="a.label"
            [data]="a.rows"
            [columns]="columns"
            [selectable]="false"
            [maxVisibleItems]="15"
          ></app-table>
          <button *ngIf="a.courseId" nbButton size="small" status="primary" type="button" (click)="selectCourse(a.courseId)">
            {{ "components.competency.openCourse" | translate }}
          </button>
        </div>
      </details>
    </div>

    <section *ngIf="behaviour" class="cp-behaviour">
      <h6>
        {{ "components.competency.behaviour.title" | translate }}
        <span class="cp-behaviour__hint">— {{ "components.competency.behaviour.hint" | translate }}</span>
      </h6>
      <p>
        {{ "components.competency.behaviour.unassisted" | translate: { correct: behaviour.correct_unassisted, answers: behaviour.answers } }}
        ·
        <ng-container *ngIf="behaviour.calibration_ready; else lowCalibration">
          {{ "components.competency.behaviour.calibration" | translate: { correct: behaviour.sure_correct, total: behaviour.sure } }}
        </ng-container>
        <ng-template #lowCalibration>{{ "components.competency.behaviour.calibrationLow" | translate }}</ng-template>
      </p>
    </section>

    <p *ngIf="retiredText" class="cp-note">{{ retiredText }}</p>
    <p *ngIf="withoutText" class="cp-note">{{ withoutText }}</p>
  </ng-container>
</ng-container>
